import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getBranding } from '../../store/actions/userActions';
import { useSubmissionUtils } from '../../utils/useSubmissionUtils';
import { useNavigate } from 'react-router';
import useAuthUtils from '../../utils/useAuthUtils';
import { ROUTES } from '../../App';
import BusyCover from '../../components/controls/BusyCover/BusyCover';
import { From } from '../../components/controls/Form';
import Button from '../../components/controls/Button';
import { TextParagraph } from '../../components/controls/Typography/TextBody';
import Heading from '../../components/controls/Typography/Heading';

export const GuestAccess = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const submissionUtils = useSubmissionUtils();
  const auth = useAuthUtils();
  const [loginError, setLoginError] = useState(false);

  /**
   *
   * @param {?import('../../store/reducers/userReducer').TDataUserGuest=} guestAccess
   * @param {?import('../../store/reducers/userReducer').TDataAuth=} auth
   */
  const resolveGuestDestination = (guestAccess, auth) => {
    if (!guestAccess) {
      setLoginError(true);
      return;
    }

    switch (true) {
      case guestAccess?.evalStatus === 'completed': {
        // user is in guest access / reuse flow and request is completed
        const documentURL =
          window.location.origin + '/recommendation/rest/viewRecommendation/' + guestAccess.evalRequestId;
        if (guestAccess.reuseForFutureEvals || guestAccess.eligibleForAutoFill) {
          // when user is in reuse flow
          submissionUtils.showSuccessSubmissionAndReuseGuestAccess(
            guestAccess.evalRequestId,
            'Thank you for submitting!',
            guestAccess.applicantFirstName,
            guestAccess.applicantLastName,
            guestAccess.instanceName,
            guestAccess.evaluationType,
            guestAccess.hasEvaluatorAccount,
            false,
            documentURL
          );
        } else {
          // when user is in guest access flow
          if (guestAccess.hasEvaluatorAccount) {
            submissionUtils.showSuccessSubmissionWithAccount(
              guestAccess.evalRequestId,
              'Thank you for submitting!', 'completed', documentURL);
          } else {
            submissionUtils.showSuccessSubmissionWithNoAccount(
              guestAccess.evalRequestId,
              'Thank you for submitting!',
              guestAccess.evalEmailAddress,
              documentURL
            );
          }
        }
        break;
      }
      case guestAccess?.errorCode === 'DECLINED': {
        if (guestAccess.hasEvaluatorAccount) {
          submissionUtils.showSuccessSubmissionWithAccount(guestAccess.evalRequestId, "You've Successfully Declined the Request", 'declined');
        } else {
          submissionUtils.showSuccessSubmissionWithNoAccount(
            guestAccess.evalRequestId,
            "You've Successfully Declined the Request",
            guestAccess.evalEmailAddress
          );
        }
        break;
      }
      case guestAccess?.errorCode === 'EXPIRED': {
        submissionUtils.pushError(
          `The request by ${guestAccess.applicantFirstName} ${guestAccess.applicantLastName} has expired.`,
          `Please contact ${guestAccess.applicantFirstName} ${guestAccess.applicantLastName} directly with any questions.`,
          'Request Expired',
          'fa-hourglass-end'
        );
        break;
      }
      case guestAccess?.errorCode === 'WITHDRAWN': {
        submissionUtils.pushError(
          'The request has been withdrawn.',
          'Please contact the applicant directly with any questions.',
          'Request Withdrawn',
          'fa-history'
        );
        break;
      }
      case auth && !!guestAccess?.evalEmailAddress: {
        dispatch(getBranding());
        navigate(ROUTES.GUEST_RECOMMENDATION_FORM + `/${guestAccess.evalRequestId}`);
        break;
      }
      default: {
        setLoginError(true);
      }
    }
  };

  const onBackClick = () => {
    navigate('/login');
  };

  useEffect(() => {
    auth.authAsGuest().then((result) => {
      resolveGuestDestination(result?.guest, result?.auth);
    })
      .catch((guestError) => {
        resolveGuestDestination(guestError);
      });
  }, []);

  return (
    <main className="lsn-page lsn-page-guest-login" aria-live="polite" title='Guest Access Login Page'>
      <Heading className="no-focus-outline text-center" tabIndex={0}>
        {loginError ? 'Login failed' : 'Logging in. Please wait ...'}
      </Heading>
      <BusyCover isBusy={auth.isAuthorizing()} />
      <From
        title='The link seems to be invalid.'
        isNarrow
        hasBorder
        isButtonsStack
        formAttr={{
          title: 'Wrong Link'
        }}
        hidden={!loginError}
        buttons={<Button
          primary
          onClick={() => onBackClick()}
        >
          Back to Login
        </Button>}
      >
        <TextParagraph>Please go back and try again.</TextParagraph>
      </From>
    </main>
  );
};

export default GuestAccess;
